// [2026-03-09] GDPR cookie consent banner
// Shows on first visit, persists decision to localStorage + server (if logged in)
import React, { useState, useEffect, useCallback } from "react";
import { getCookieConsent, setCookieConsent, ConsentValue } from "../../utils/cookieConsent";
import { loadAnalytics } from "../../utils/analyticsLoader";
import { syncCookieConsentToServer } from "../../services/usersService";
import styles from "./CookieConsentBanner.module.scss";

interface CookieConsentBannerProps {
  isLoggedIn: boolean;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ isLoggedIn }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setVisible(true);
    } else if (consent.value === "accepted") {
      // Returning visitor who already accepted — load analytics
      loadAnalytics();
    }
  }, []);

  const handleConsent = useCallback(
    (value: ConsentValue) => {
      setCookieConsent(value);
      setVisible(false);

      if (value === "accepted") {
        loadAnalytics();
      }

      // Sync to server if user is logged in (fire-and-forget)
      if (isLoggedIn) {
        syncCookieConsentToServer(value).catch(() => {
          // Non-critical — consent is stored locally regardless
        });
      }
    },
    [isLoggedIn],
  );

  if (!visible) return null;

  return (
    <div className={styles.banner} role="dialog" aria-label="Cookie consent">
      <div className={styles.content}>
        <h3 className={`${styles.title} u-P1-18-semi-bold`}>We use cookies to improve your experience</h3>
        <p className={`${styles.body} u-P2-16`}>
          This site uses Google Analytics cookies to help us understand how you use the platform.
          No advertising or tracking cookies are used.
        </p>
        <div className={styles.actions}>
          <button className={`${styles.acceptBtn} btn-primary`} onClick={() => handleConsent("accepted")}>
            Accept
          </button>
          <button className={`${styles.declineBtn} btn-figma-1`} onClick={() => handleConsent("declined")}>
            Decline
          </button>
        </div>
        <a
          href="https://uavtradezone.com/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.privacyLink} u-P3-14-medium`}
        >
          Privacy Policy
        </a>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
